import { useState } from "react";

import { api, ApiError, UserMe } from "../api";
import { PageHeader } from "../ui";

const SUPPORT = "support-nh@factor2s.ai";
const INTERESTS: [string, string][] = [
  ["pro", "Institutional plan"],
  ["custom", "Custom or team plan"],
  ["subscriber", "My NilssonHedge subscription"],
  ["other", "Something else"],
];
const CONTROL =
  "w-full px-3 py-2.5 rounded-md text-[15px] font-sans outline-none transition-colors bg-input border border-input-border text-main focus:border-accent";
const FALLBACK = `Your message couldn't be sent. Please email ${SUPPORT} instead.`;

/** The server's message: a string detail, or the first field error from validation. */
export function errorText(e: unknown): string {
  if (!(e instanceof ApiError)) return FALLBACK;
  try {
    const d = JSON.parse(e.message).detail;
    if (typeof d === "string") return d;
    if (Array.isArray(d) && d[0]?.msg) {
      const msg = String(d[0].msg);
      return msg.startsWith("value is not a valid email") ? "Please enter a valid email address." : msg.replace(/^Value error, /, "");
    }
  } catch {
    // not JSON
  }
  return FALLBACK;
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-main">{label}</span>
      {children}
      {hint && <span className="text-xs text-muted">{hint}</span>}
    </label>
  );
}

/** Public contact form; `?interest=pro|custom|subscriber` preselects the topic. */
export function Contact({ user }: { user: UserMe | null }) {
  const [openedAt] = useState(Date.now);
  const asked = new URLSearchParams(window.location.search).get("interest");
  const [form, setForm] = useState({
    name: user?.name ?? "",
    email: user?.email ?? "",
    company: "",
    interest: INTERESTS.some(([k]) => k === asked) ? asked! : "other",
    message: "",
    website: "",
  });
  const [state, setState] = useState<"form" | "sending" | "sent">("form");
  const [error, setError] = useState<string | null>(null);

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm({ ...form, [key]: e.target.value });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setState("sending");
    setError(null);
    try {
      await api.post("/contact", { ...form, elapsed_ms: Date.now() - openedAt });
      setState("sent");
    } catch (err) {
      setError(errorText(err));
      setState("form");
    }
  }

  return (
    <section className="flex flex-col gap-8 max-w-2xl">
      <PageHeader
        title="Talk to us"
        description={<>Questions about the Institutional plan, a team plan or your access? Send us a note, or email <a href={`mailto:${SUPPORT}`} className="whitespace-nowrap">{SUPPORT}</a>.</>}
      />

      {state === "sent" ? (
        <div className="p-6 rounded-lg bg-accent/10 text-accent">
          <p className="font-semibold">Thanks, your message is on its way.</p>
          <p className="mt-1 text-sm">We'll reply to {form.email}.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="relative flex flex-col gap-5 p-8 max-sm:p-5 rounded-lg bg-card border border-border">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Name">
              <input required maxLength={100} autoComplete="name" value={form.name} onChange={set("name")} className={CONTROL} />
            </Field>
            <Field label="Email">
              <input required type="email" maxLength={254} autoComplete="email" value={form.email} onChange={set("email")} className={CONTROL} />
            </Field>
            <Field label="Company (optional)">
              <input maxLength={150} autoComplete="organization" value={form.company} onChange={set("company")} className={CONTROL} />
            </Field>
            <Field label="About">
              <select value={form.interest} onChange={set("interest")} className={CONTROL}>
                {INTERESTS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Message" hint="Plain text only, no links.">
            <textarea required minLength={10} maxLength={4000} rows={6} value={form.message} onChange={set("message")} className={CONTROL} />
          </Field>

          {/* Spam trap: off-screen and skipped by keyboard and screen readers, so only bots fill it. */}
          <div aria-hidden="true" className="absolute -left-[9999px] w-px h-px overflow-hidden">
            <label>
              Leave this field empty
              <input tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
            </label>
          </div>

          {error && <p className="text-sm text-neg">{error}</p>}
          <div>
            <button type="submit" disabled={state === "sending"} className="btn-primary text-white">
              {state === "sending" ? "Sending…" : "Send message"}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
